/**
 * BackOffice Dedupe Helpers
 * 
 * Domain-safe deduplication rules for BackOffice offline queue items.
 * Conservative approach - prefer extra queue items over unsafe merging.
 */

import { supportsDeduplication } from './backofficePolicies';

/**
 * Build semantic key for BackOffice queue items
 * Used to identify items that can be collapsed
 * 
 * @param {Object} item - Queue item
 * @returns {string} Semantic key
 */
export function buildBackofficeSemanticKey(item) {
  const { type, payload, entityType, entityId, merchantId } = item;
  
  // Base key components
  const merchant = merchantId || payload?.merchantId || payload?.restaurant_id || 'default';
  const entity = entityId || payload?.entityId || payload?.id || 'default';
  const eType = entityType || payload?.entityType || 'generic';
  
  // Build key based on action type
  switch (type) {
    case 'BUSINESS_PROFILE_UPDATE':
      return `backoffice:${merchant}:business_profile`;
      
    case 'LOCATION_HOURS_UPDATE':
      return `backoffice:${merchant}:hours:${payload?.branch_id || 'main'}`;
      
    case 'STAFF_PROFILE_UPDATE':
      return `backoffice:${merchant}:staff:${payload?.staff_id || entity}`;
      
    case 'NOTIFICATION_PREFS_UPDATE':
      return `backoffice:${merchant}:notifications`;
      
    case 'ORDER_SETTINGS_UPDATE':
      return `backoffice:${merchant}:order_settings:${payload?.branch_id || 'main'}`;
      
    case 'PRINTER_CONFIG_UPDATE':
      return `backoffice:${merchant}:printer:${payload?.printer_id || entity}`;
      
    case 'DISPLAY_PREFS_UPDATE':
      return `backoffice:${merchant}:display_prefs`;
      
    case 'MENU_ITEM_UPDATE':
      return `backoffice:${merchant}:menu_item:${payload?.item_id || entity}`;
      
    case 'MENU_CATEGORY_UPDATE':
      return `backoffice:${merchant}:menu_category:${payload?.category_id || entity}`;
      
    case 'INVENTORY_NOTE_UPDATE':
      return `backoffice:${merchant}:inventory:${payload?.item_id || entity}`;
      
    case 'DISCOUNT_UPDATE':
      return `backoffice:${merchant}:discount:${payload?.discount_id || entity}`;
      
    case 'TAX_SETTINGS_UPDATE':
      return `backoffice:${merchant}:tax_settings`;
      
    case 'SERVICE_FEE_UPDATE':
      return `backoffice:${merchant}:service_fee`;
      
    default:
      // Generic fallback - use type + entity
      return `backoffice:${merchant}:${type}:${eType}:${entity}`;
  }
}

/**
 * Check if two BackOffice queue items can be collapsed
 * 
 * Rules:
 * - Same semantic key (same entity + action type)
 * - Both are pending status
 * - Action type supports deduplication
 * - Not blocked or high-risk actions
 * 
 * @param {Object} a - First queue item
 * @param {Object} b - Second queue item
 * @returns {boolean}
 */
export function canCollapseBackofficeUpdate(a, b) {
  // Only collapse pending items
  if (a.status !== 'pending' || b.status !== 'pending') {
    return false;
  }
  
  // Must be same action type
  if (a.type !== b.type) {
    return false;
  }
  
  // Action type must support deduplication
  if (!supportsDeduplication(a.type)) {
    return false;
  }
  
  // Compare semantic keys
  const keyA = buildBackofficeSemanticKey(a);
  const keyB = buildBackofficeSemanticKey(b);
  
  return keyA === keyB;
}

/**
 * Merge two BackOffice pending updates
 * Latest intent wins
 * 
 * @param {Object} prev - Previous (older) queue item
 * @param {Object} next - New queue item
 * @returns {Object} Merged queue item
 */
export function mergeBackofficePendingUpdate(prev, next) {
  return {
    // Keep original ID and creation time
    id: prev.id,
    createdAt: prev.createdAt,
    
    // Use new payload and metadata
    type: next.type,
    payload: next.payload,
    endpoint: next.endpoint,
    method: next.method,
    
    // Update timestamps
    updatedAt: next.createdAt || Date.now(),
    clientGeneratedAt: next.clientGeneratedAt,
    
    // Use new idempotency key
    idempotencyKey: next.idempotencyKey,
    
    // Reset retry state
    status: 'pending',
    retryCount: 0,
    nextRetryAt: Date.now(),
    lastError: null,
    
    // Preserve entity info
    entityType: next.entityType || prev.entityType,
    entityId: next.entityId || prev.entityId,
    merchantId: next.merchantId || prev.merchantId,
    userId: next.userId || prev.userId,
    
    // Merge metadata
    _merged: true,
    _mergedAt: Date.now(),
    _previousIdempotencyKey: prev.idempotencyKey,
    _mergeCount: (prev._mergeCount || 0) + 1,
    
    // Preserve module info for review
    module: next.module || prev.module,
    screen: next.screen || prev.screen
  };
}

/**
 * Check if item is a BackOffice action
 * @param {Object} item - Queue item
 * @returns {boolean}
 */
export function isBackofficeAction(item) {
  const backofficeTypes = [
    'BUSINESS_PROFILE_UPDATE',
    'LOCATION_HOURS_UPDATE',
    'STAFF_PROFILE_UPDATE',
    'NOTIFICATION_PREFS_UPDATE',
    'ORDER_SETTINGS_UPDATE',
    'PRINTER_CONFIG_UPDATE',
    'DISPLAY_PREFS_UPDATE',
    'MENU_ITEM_UPDATE',
    'MENU_CATEGORY_UPDATE',
    'INVENTORY_NOTE_UPDATE',
    'DISCOUNT_UPDATE',
    'TAX_SETTINGS_UPDATE',
    'SERVICE_FEE_UPDATE',
    'TIP_POOL_RULES_UPDATE'
  ];
  
  return backofficeTypes.includes(item.type);
}

/**
 * Get display name for BackOffice action type
 * @param {string} actionType
 * @returns {string}
 */
export function getBackofficeActionDisplayName(actionType) {
  const names = {
    BUSINESS_PROFILE_UPDATE: 'Business Profile',
    LOCATION_HOURS_UPDATE: 'Operating Hours',
    STAFF_PROFILE_UPDATE: 'Staff Profile',
    NOTIFICATION_PREFS_UPDATE: 'Notifications',
    ORDER_SETTINGS_UPDATE: 'Order Settings',
    PRINTER_CONFIG_UPDATE: 'Printer Config',
    DISPLAY_PREFS_UPDATE: 'Display Preferences',
    MENU_ITEM_UPDATE: 'Menu Item',
    MENU_CATEGORY_UPDATE: 'Menu Category',
    INVENTORY_NOTE_UPDATE: 'Inventory Note',
    DISCOUNT_UPDATE: 'Discount',
    TAX_SETTINGS_UPDATE: 'Tax Settings',
    SERVICE_FEE_UPDATE: 'Service Fee',
    TIP_POOL_RULES_UPDATE: 'Tip Pool Rules'
  };
  
  return names[actionType] || actionType;
}

/**
 * Get module name for action type
 * @param {string} actionType
 * @returns {string}
 */
export function getBackofficeModule(actionType) {
  const modules = {
    BUSINESS_PROFILE_UPDATE: 'Settings',
    LOCATION_HOURS_UPDATE: 'Settings',
    STAFF_PROFILE_UPDATE: 'Staff',
    NOTIFICATION_PREFS_UPDATE: 'Settings',
    ORDER_SETTINGS_UPDATE: 'Settings',
    PRINTER_CONFIG_UPDATE: 'Kitchen',
    DISPLAY_PREFS_UPDATE: 'Settings',
    MENU_ITEM_UPDATE: 'Menu',
    MENU_CATEGORY_UPDATE: 'Menu',
    INVENTORY_NOTE_UPDATE: 'Inventory',
    DISCOUNT_UPDATE: 'Discounts',
    TAX_SETTINGS_UPDATE: 'Settings',
    SERVICE_FEE_UPDATE: 'Settings',
    TIP_POOL_RULES_UPDATE: 'Payroll'
  };
  
  return modules[actionType] || 'Other';
}
